import { buttonFocusStyle } from '@/components/button-link/button-link';
import { styled } from '@/stitches.config';
import { MouseEventHandler } from 'react';

interface FilterItemProps {
  selected: boolean;
  label: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  as?: 'button' | 'a';
}

export function FilterItem({
  selected,
  label,
  onClick,
  as = 'button',
}: FilterItemProps) {
  return (
    <Wrapper as={as} onClick={onClick}>
      <ButtonContent selected={selected}>
        <Label>{label}</Label>
      </ButtonContent>
      <LeftHoverBlock aria-hidden="true">
        <Label>{label}</Label>
      </LeftHoverBlock>
      <RightHoverBlock aria-hidden="true">
        <Label>{label}</Label>
      </RightHoverBlock>
    </Wrapper>
  );
}

const Label = styled('span', {
  position: 'relative',
  padding: '$3 $3',

  '@bp2': {
    padding: '$3 0',
  },
});

const sharedStyles = {
  width: '100%',
  display: 'inline-flex',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  textDecoration: 'none',

  '@bp2': {
    justifyContent: 'center',
  },
};

const LeftHoverBlock = styled('div', {
  ...sharedStyles,

  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: '$foreground',

  clipPath: 'polygon(0px 100%, 50% 100%, 50% 100%, 0px 100%)', // rectangle with no height
  transition: 'clip-path 320ms ease-out',

  [`> ${Label}`]: {
    color: '$background',
    position: 'relative',
    zIndex: '$default',
  },
});

const RightHoverBlock = styled('div', {
  ...sharedStyles,

  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: '$foreground',

  clipPath:
    'polygon(calc(50% - 1px) 100%, 100% 100%, 100% 100%, calc(50% - 1px) 100%)', // rectangle with no height
  transition: 'clip-path 320ms ease-out',
  transitionDelay: '150ms',

  [`> ${Label}`]: {
    color: '$background',
    position: 'relative',
    zIndex: '$default',
  },
});

const leftBlockHoverStyles = {
  [`${LeftHoverBlock}`]: {
    clipPath: 'polygon(0px 0px, 50% 0px, 50% 100%, 0px 100%)', // rectangle with full height
  },
};

const rightBlockHoverStyles = {
  [`${RightHoverBlock}`]: {
    clipPath:
      'polygon(calc(50% - 1px) 0, 100% 0, 100% 100%, calc(50% - 1px) 100%)', // rectangle with full height
  },
};

const ButtonContent = styled('div', {
  ...sharedStyles,

  position: 'relative',
  color: '$foreground',
  marginBottom: 0,

  '&:active': {
    background: '$gray4',
  },

  variants: {
    selected: {
      true: {
        background: '$foreground',
        [`> ${Label}`]: {
          color: '$background',
        },
      },
    },
  },
});

const Wrapper = styled('a', {
  position: 'relative',
  width: '100%',
  display: 'inline-block',
  textDecoration: 'none',
  background: '$white',
  borderBottom: '1px solid $gray8',

  '@bp2': {
    width: 'var(--item-width)',
    borderBottom: 'none',
  },

  '&:hover, &:active': {
    ...leftBlockHoverStyles,
    ...rightBlockHoverStyles,
  },

  '&:focus-visible': {
    ...buttonFocusStyle,
    ...leftBlockHoverStyles,
    ...rightBlockHoverStyles,
  },

  variants: {
    isFirst: {
      true: {
        borderTop: '1px solid $gray8',
        '@bp2': {
          borderTop: 'none',
        },
      },
    },
  },
});
